import React, { useState, useEffect } from 'react';
import Header from './components/common/Header';
import Navigation from './components/common/Navigation';
import Footer from './components/common/Footer';
import CategoryGrid from './components/categories/CategoryGrid';
import QuizCard from './components/quiz/QuizCard';
import QuizResults from './components/quiz/QuizResults';
import ParentalDashboard from './components/dashboard/ParentalDashboard';
import RewardModal from './components/rewards/RewardModal';
import ProfileSelector from './components/profiles/ProfileSelector';
import ProfileManager from './components/profiles/ProfileManager';
import DifficultyModal from './components/difficulty/DifficultyModal';
import QuizModeModal from './components/quiz/QuizModeModal';
import Tutorial, { shouldShowTutorial } from './components/onboarding/Tutorial';
import StreakTracker from './components/dashboard/StreakTracker';
import ActivityCalendar from './components/dashboard/ActivityCalendar';
import LevelUpModal from './components/gamification/LevelUpModal';
import XPGainedModal from './components/gamification/XPGainedModal';
import Leaderboard from './components/gamification/Leaderboard';
import GameZone from './components/gamezone/GameZone';
import { loadProgress, saveProgress } from './utils/storage';
import { playSound } from './utils/soundManager';
import { questions } from './data/questions';
import { getTranslatedQuestions } from './data/translatedQuestions';
import { badges } from './data/badges';
import { useLanguage } from './contexts/LanguageContext';
import { getActiveProfile, updateProfileProgress } from './utils/profileManager';
import { getQuestionsForDifficulty, loadDifficultyPreference, saveDifficultyPreference, DIFFICULTY_LEVELS } from './utils/difficultyManager';
import { QUIZ_MODES, loadQuizModePreference, saveQuizModePreference } from './utils/timerManager';
import { checkAndUpdateStreak, loadStreakData } from './utils/streakManager';
import { 
  loadGamificationData, 
  calculateQuizXP, 
  awardXP, 
  updateTodayStats,
  checkChallengeCompletion,
  completeDailyChallenge,
  getDailyChallenges
} from './utils/gamificationManager';
import './App.css';
import './styles/animations.css';
import './components/profiles/profiles.css';
import './components/difficulty/difficulty.css';
import './components/quiz/timer.css';
import './components/dashboard/streak.css';
import './components/onboarding/onboarding.css';
import './components/gamification/gamification.css';
import './components/gamezone/gamezone.css';
import './components/common/footer.css';

function App() {
  const { currentLanguage } = useLanguage();
  const [currentProfile, setCurrentProfile] = useState(null);
  const [showProfileManager, setShowProfileManager] = useState(false);
  const [showDifficultyModal, setShowDifficultyModal] = useState(false);
  const [showQuizModeModal, setShowQuizModeModal] = useState(false);
  const [currentDifficulty, setCurrentDifficulty] = useState(DIFFICULTY_LEVELS.MEDIUM);
  const [currentQuizMode, setCurrentQuizMode] = useState(QUIZ_MODES.PRACTICE);
  const [currentView, setCurrentView] = useState('home');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [quizQuestions, setQuizQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [userAnswers, setUserAnswers] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [userProgress, setUserProgress] = useState({});
  const [earnedBadges, setEarnedBadges] = useState([]);
  const [showRewardModal, setShowRewardModal] = useState(false);
  const [newBadge, setNewBadge] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(false);
  
  // Streak & Gamification
  const [streakData, setStreakData] = useState({});
  const [showActivityCalendar, setShowActivityCalendar] = useState(false);
  const [gamificationData, setGamificationData] = useState({});
  const [showLevelUpModal, setShowLevelUpModal] = useState(false);
  const [newLevel, setNewLevel] = useState(1);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [xpGained, setXpGained] = useState(0);
  const [showXPModal, setShowXPModal] = useState(false);
  const [isTimedOut, setIsTimedOut] = useState(false);
  
  // Tutorial
  const [showTutorial, setShowTutorial] = useState(false);

  useEffect(() => {
    // Check for active profile
    const activeProfile = getActiveProfile();
    if (activeProfile) {
      loadProfileData(activeProfile);
    }
  }, []);

  useEffect(() => {
    document.documentElement.className = isDarkMode ? 'dark' : '';
  }, [isDarkMode]);

  // Reload gamification data when switching to gamezone view
  useEffect(() => {
    if (currentView === 'gamezone' && currentProfile) {
      const freshGamificationData = loadGamificationData(currentProfile.id);
      setGamificationData(freshGamificationData);
    }
  }, [currentView, currentProfile]);

  const loadProfileData = (profile) => {
    setCurrentProfile(profile);
    setUserProgress(profile.userProgress || {});
    setEarnedBadges(profile.earnedBadges || []);
    setIsDarkMode(profile.settings?.darkMode || false);
    
    // Load difficulty preference
    const savedDifficulty = loadDifficultyPreference(profile.id);
    setCurrentDifficulty(savedDifficulty);
    
    // Load quiz mode preference
    const savedQuizMode = loadQuizModePreference(profile.id);
    setCurrentQuizMode(savedQuizMode);
    
    // Load streak data
    const streak = loadStreakData(profile.id);
    setStreakData(streak);
    
    // Load gamification data
    const gamification = loadGamificationData(profile.id);
    setGamificationData(gamification);
    
    // Check if tutorial should show
    if (shouldShowTutorial(profile.id)) {
      setShowTutorial(true);
    }
  };

  const handleProfileSelect = (profile) => {
    loadProfileData(profile);
  };

  const handleProfileChange = (profile) => {
    loadProfileData(profile);
    setCurrentView('home');
    setShowResults(false);
  };

  const handleLogout = () => {
    setCurrentProfile(null);
    setUserProgress({});
    setEarnedBadges([]);
    setCurrentView('home');
    setShowResults(false);
  };

  const handleManageProfiles = () => {
    setShowProfileManager(true);
  };

  const handleCloseProfileManager = () => {
    setShowProfileManager(false);
    // Reload current profile data
    if (currentProfile) {
      const updatedProfile = getActiveProfile();
      if (updatedProfile) {
        loadProfileData(updatedProfile);
      }
    }
  };

  const startQuiz = (category) => {
    // Get questions based on difficulty level and language
    const translatedCategoryQuestions = getTranslatedQuestions(category, currentLanguage);
    const categoryQuestions = translatedCategoryQuestions || questions[category] || [];
    const difficultyQuestions = getQuestionsForDifficulty(categoryQuestions, currentDifficulty);
    
    setSelectedCategory(category);
    setQuizQuestions(difficultyQuestions);
    setCurrentQuestionIndex(0);
    setScore(0);
    setUserAnswers([]);
    setShowResults(false);
    setShowLevelUpModal(false); // Reset level up modal when starting new quiz
    setIsTimedOut(false); // Reset timeout flag
    setCurrentView('quiz');
    playSound('start');
  };

  const handleAnswer = (selectedAnswer, timeSpent = 0, timeBonus = 0) => {
    // Prevent answering beyond quiz length
    if (currentQuestionIndex >= quizQuestions.length) {
      return;
    }
    
    const currentQuestion = quizQuestions[currentQuestionIndex];
    
    // If timeout (selectedAnswer is null), end quiz immediately with 0 XP
    if (selectedAnswer === null) {
      setIsTimedOut(true);
      playSound('incorrect');
      setTimeout(() => {
        finishQuizWithTimeout();
      }, 1000);
      return;
    }
    
    const isCorrect = selectedAnswer === currentQuestion.correct;
    
    // Calculate new score immediately (only count correct answers, no bonus)
    const newScore = isCorrect ? score + 1 : score;
    
    if (isCorrect) {
      setScore(newScore);
      playSound('correct');
    } else {
      playSound('incorrect');
    }

    const newUserAnswers = [...userAnswers, {
      question: currentQuestion.question,
      selected: selectedAnswer,
      correct: currentQuestion.correct,
      isCorrect,
      timeSpent,
      timeBonus
    }];
    
    setUserAnswers(newUserAnswers);

    setTimeout(() => {
      // Check if this was the last question
      const isLastQuestion = currentQuestionIndex >= quizQuestions.length - 1;
      
      if (isLastQuestion) {
        // Pass the new score directly to avoid state timing issues
        finishQuiz(newScore, newUserAnswers);
      } else {
        setCurrentQuestionIndex(currentQuestionIndex + 1);
      }
    }, 1000);
  };

  const finishQuizWithTimeout = () => {
    // Quiz timed out - show results with 0 XP
    setXpGained(0);
    setShowXPModal(true);
    setShowResults(true);
  };

  const finishQuiz = (finalScore = score, answersArray = userAnswers) => {
    const totalQuestions = quizQuestions.length;
    const percentage = Math.round((finalScore / totalQuestions) * 100);
    const isPerfect = finalScore === totalQuestions;
    
    // Update progress
    const newProgress = {
      ...userProgress,
      [selectedCategory]: {
        ...userProgress[selectedCategory],
        totalQuizzes: (userProgress[selectedCategory]?.totalQuizzes || 0) + 1,
        totalScore: (userProgress[selectedCategory]?.totalScore || 0) + finalScore,
        totalPossibleScore: (userProgress[selectedCategory]?.totalPossibleScore || 0) + totalQuestions,
        bestScore: Math.max(userProgress[selectedCategory]?.bestScore || 0, percentage),
        lastPlayed: new Date().toISOString()
      }
    };

    setUserProgress(newProgress);
    
    // Update streak
    if (currentProfile) {
      const { data: updatedStreak } = checkAndUpdateStreak(currentProfile.id);
      setStreakData(updatedStreak);
      
      // Calculate and award XP
      const xpEarned = calculateQuizXP({
        correctAnswers: finalScore,
        totalQuestions,
        difficulty: currentDifficulty,
        quizMode: currentQuizMode,
        isPerfect,
        combo: 0 // Can be enhanced with combo tracking
      });
      
      const xpResult = awardXP(currentProfile.id, xpEarned);
      
      // Use the XP data from xpResult instead of reloading
      const updatedGamificationData = {
        totalXP: xpResult.totalXP,
        level: xpResult.newLevel,
        dailyChallenges: gamificationData.dailyChallenges || {},
        todayStats: gamificationData.todayStats || {
          quizzesToday: 0,
          categoriesPlayed: [],
          maxCombo: 0,
          lastPlayedDate: null
        }
      };
      
      setGamificationData(updatedGamificationData);
      
      // Show level up modal ONLY if actually leveled up
      if (xpResult.leveledUp && xpResult.newLevel > xpResult.oldLevel) {
        setNewLevel(xpResult.newLevel);
        // Level up modal will show after XP modal is closed
      }
      
      // Update today's stats
      updateTodayStats(currentProfile.id, {
        quizCompleted: true,
        category: selectedCategory,
        difficulty: currentDifficulty,
        mode: currentQuizMode
      });
    }
    
    // Check for new badges
    const newBadges = checkForBadges(newProgress);
    if (newBadges.length > 0) {
      setNewBadge(newBadges[0]);
      setEarnedBadges([...earnedBadges, ...newBadges]);
      setShowRewardModal(true);
      playSound('badge');
    }
    
    // Show XP gained modal after everything else (so it appears on top)
    if (currentProfile) {
      const xpEarned = calculateQuizXP({ 
        correctAnswers: finalScore,
        quizMode: currentQuizMode,
        difficulty: currentDifficulty
      });
      setXpGained(xpEarned);
      setShowXPModal(true);
    }

    // Save progress to profile
    if (currentProfile) {
      updateProfileProgress(currentProfile.id, {
        userProgress: newProgress,
        earnedBadges: [...earnedBadges, ...newBadges]
      });
    }

    setShowResults(true);
  };

  const checkForBadges = (progress) => {
    const newBadges = [];
    
    badges.forEach(badge => {
      if (!earnedBadges.find(eb => eb.id === badge.id)) {
        let earned = false;
        
        switch(badge.condition.type) {
          case 'category_score':
            const categoryScore = progress[badge.condition.category]?.bestScore || 0;
            earned = categoryScore >= badge.condition.value;
            break;
            
          case 'total_quizzes':
            const totalQuizzes = Object.values(progress).reduce((sum, cat) => 
              sum + (cat.totalQuizzes || 0), 0);
            earned = totalQuizzes >= badge.condition.value;
            break;
            
          case 'perfect_scores':
            const perfectScores = Object.values(progress).reduce((sum, cat) => 
              sum + (cat.bestScore === 100 ? 1 : 0), 0);
            earned = perfectScores >= badge.condition.value;
            break;
            
          case 'category_perfect':
            earned = progress[badge.condition.category]?.bestScore === 100;
            break;
            
          case 'category_quizzes':
            const catQuizzes = progress[badge.condition.category]?.totalQuizzes || 0;
            earned = catQuizzes >= badge.condition.value;
            break;
            
          case 'categories_tried':
            const categoriesTried = Object.keys(progress).length;
            earned = categoriesTried >= badge.condition.value;
            break;
            
          case 'consecutive_perfects':
            // This would need additional tracking - simplified for now
            earned = false;
            break;
            
          case 'score_improvement':
            // This would need historical tracking - simplified for now
            earned = false;
            break;
            
          case 'quizzes_per_day':
            // This would need date tracking - simplified for now
            earned = false;
            break;
            
          default:
            break;
        }
        
        if (earned) {
          newBadges.push(badge);
        }
      }
    });
    
    return newBadges;
  };

  const toggleTheme = () => {
    const newTheme = !isDarkMode;
    setIsDarkMode(newTheme);
    if (currentProfile) {
      updateProfileProgress(currentProfile.id, {
        userProgress,
        earnedBadges
      });
    }
  };

  const resetQuiz = () => {
    setCurrentQuestionIndex(0);
    setScore(0);
    setUserAnswers([]);
    setShowResults(false);
    setShowLevelUpModal(false); // Reset level up modal
    setIsTimedOut(false); // Reset timeout flag
  };

  const goHome = () => {
    setCurrentView('home');
    setSelectedCategory(null);
    setShowResults(false);
    setShowLevelUpModal(false); // Reset level up modal
    setIsTimedOut(false); // Reset timeout flag
  };

  const handleDifficultyChange = (newDifficulty) => {
    setCurrentDifficulty(newDifficulty);
    if (currentProfile) {
      saveDifficultyPreference(currentProfile.id, newDifficulty);
    }
    playSound('success');
  };

  const handleOpenDifficultyModal = () => {
    setShowDifficultyModal(true);
    playSound('click');
  };

  const handleCloseDifficultyModal = () => {
    setShowDifficultyModal(false);
  };

  const handleQuizModeChange = (newMode) => {
    setCurrentQuizMode(newMode);
    if (currentProfile) {
      saveQuizModePreference(currentProfile.id, newMode);
    }
    playSound('success');
  };

  const handleOpenQuizModeModal = () => {
    setShowQuizModeModal(true);
    playSound('click');
  };

  const handleCloseQuizModeModal = () => {
    setShowQuizModeModal(false);
  };

  // Tutorial handlers
  const handleTutorialComplete = () => {
    setShowTutorial(false);
  };

  const handleTutorialSkip = () => {
    setShowTutorial(false);
  };

  // Streak handlers
  const handleShowActivityCalendar = () => {
    setShowActivityCalendar(true);
    playSound('click');
  };

  const handleCloseActivityCalendar = () => {
    setShowActivityCalendar(false);
  };

  // Gamification handlers
  const handleShowLeaderboard = () => {
    setShowLeaderboard(true);
    playSound('click');
  };

  const handleCloseLeaderboard = () => {
    setShowLeaderboard(false);
  };

  const handleCloseLevelUp = () => {
    setShowLevelUpModal(false);
  };

  const handleCloseXPModal = () => {
    setShowXPModal(false);
    
    // If timed out, go back to home or reset quiz
    if (isTimedOut) {
      goHome();
      return;
    }
    
    // Show level up modal if leveled up
    if (gamificationData.level > (newLevel - 1)) {
      setShowLevelUpModal(true);
    }
  };

  const handleMiniGameComplete = (gameResult) => {
    if (!currentProfile || !gameResult.completed) {
      return; // No XP if game not completed
    }

    // Award XP based on game score (1 XP per point)
    const xpEarned = gameResult.score || 0;
    
    if (xpEarned > 0) {
      const xpResult = awardXP(currentProfile.id, xpEarned);
      
      // Update gamification data
      const updatedGamificationData = {
        totalXP: xpResult.totalXP,
        level: xpResult.newLevel,
        dailyChallenges: gamificationData.dailyChallenges || {},
        todayStats: gamificationData.todayStats || {}
      };
      
      setGamificationData(updatedGamificationData);
      
      // Show XP modal
      setXpGained(xpEarned);
      setShowXPModal(true);
      
      // Check for level up
      if (xpResult.leveledUp && xpResult.newLevel > xpResult.oldLevel) {
        setNewLevel(xpResult.newLevel);
      }
    }
  };

  // Show profile selector if no active profile
  if (!currentProfile) {
    return <ProfileSelector onProfileSelect={handleProfileSelect} />;
  }

  // Show profile manager if requested
  if (showProfileManager) {
    return (
      <ProfileManager
        onClose={handleCloseProfileManager}
        onProfilesUpdated={() => {}}
      />
    );
  }

  return (
    <div className={`app ${isDarkMode ? 'dark' : ''}`}>
      <Header 
        isDarkMode={isDarkMode} 
        toggleTheme={toggleTheme}
        earnedBadges={earnedBadges}
        currentProfile={currentProfile}
        onProfileChange={handleProfileChange}
        onLogout={handleLogout}
        onManageProfiles={handleManageProfiles}
        currentDifficulty={currentDifficulty}
        onOpenDifficultyModal={handleOpenDifficultyModal}
        currentQuizMode={currentQuizMode}
        onOpenQuizModeModal={handleOpenQuizModeModal}
      />
      
      <Navigation 
        currentView={currentView}
        setCurrentView={setCurrentView}
        goHome={goHome}
      />

      <main className="main-content">
        {currentView === 'home' && (
          <CategoryGrid onSelectCategory={startQuiz} />
        )}

        {currentView === 'quiz' && selectedCategory && !showResults && quizQuestions.length > 0 && (
          <QuizCard
            category={selectedCategory}
            question={quizQuestions[currentQuestionIndex]}
            questionNumber={currentQuestionIndex + 1}
            totalQuestions={quizQuestions.length}
            onAnswer={handleAnswer}
            score={score}
            quizMode={currentQuizMode}
            difficulty={currentDifficulty}
          />
        )}

        {currentView === 'quiz' && showResults && (
          <QuizResults
            category={selectedCategory}
            score={score}
            totalQuestions={quizQuestions.length}
            answers={userAnswers}
            onRestart={resetQuiz}
            onHome={goHome}
            difficulty={currentDifficulty}
            quizMode={currentQuizMode}
          />
        )}

        {currentView === 'gamezone' && (
          <GameZone
            gamificationData={gamificationData}
            streakData={streakData}
            currentProfile={currentProfile}
            onShowLeaderboard={handleShowLeaderboard}
            onShowActivityCalendar={handleShowActivityCalendar}
            onGameComplete={handleMiniGameComplete}
          />
        )}

        {currentView === 'dashboard' && (
          <ParentalDashboard 
            userProgress={userProgress}
            earnedBadges={earnedBadges}
            streakData={streakData}
            gamificationData={gamificationData}
            currentProfile={currentProfile}
            onShowActivityCalendar={handleShowActivityCalendar}
            onShowLeaderboard={handleShowLeaderboard}
          />
        )}
      </main>

      {showRewardModal && newBadge && (
        <RewardModal
          badge={newBadge}
          onClose={() => setShowRewardModal(false)}
        />
      )}

      {showDifficultyModal && (
        <DifficultyModal
          isOpen={showDifficultyModal}
          onClose={handleCloseDifficultyModal}
          currentDifficulty={currentDifficulty}
          onSelect={handleDifficultyChange}
          userAge={currentProfile?.age}
          userProgress={userProgress}
        />
      )}

      {showQuizModeModal && (
        <QuizModeModal
          isOpen={showQuizModeModal}
          onClose={handleCloseQuizModeModal}
          currentMode={currentQuizMode}
          onSelect={handleQuizModeChange}
          difficulty={currentDifficulty}
        />
      )}

      {showTutorial && (
        <Tutorial
          onComplete={handleTutorialComplete}
          onSkip={handleTutorialSkip}
          profileId={currentProfile?.id}
        />
      )}

      {showActivityCalendar && (
        <ActivityCalendar
          activityDates={streakData.activityDates || []}
          onClose={handleCloseActivityCalendar}
        />
      )}

      {showXPModal && (
        <XPGainedModal
          xpGained={xpGained}
          currentLevel={gamificationData.level || 1}
          totalXP={gamificationData.totalXP || 0}
          onClose={handleCloseXPModal}
          isTimeout={isTimedOut}
        />
      )}

      {showLevelUpModal && (
        <LevelUpModal
          newLevel={newLevel}
          onClose={handleCloseLevelUp}
        />
      )}

      {showLeaderboard && (
        <Leaderboard
          currentProfileId={currentProfile?.id}
          onClose={handleCloseLeaderboard}
        />
      )}

      <Footer />
    </div>
  );
}

export default App;